/**
 * CG Boost API client.
 *
 * Mirrors the route shapes in backend/src/routes/cgBoost.ts. All functions
 * inject the Firebase ID token via the shared `apiRequest` from api.ts;
 * permission gating happens server-side.
 */

import { apiRequest } from './api';
import type {
  CgBoostCampaign,
  CgBoostCampaignStatus,
  CgBoostChangeLogEntry,
  CgBoostPlatform,
  CgBoostPlatformIds,
  CgBoostPullStatus,
  CgBoostValidateLinkResult,
  CreateCampaignRequest,
  CreativeGroupSuggestion,
  CreativePayload,
  IntelligencePayload,
  MomentumPayload,
  PlatformsPayload,
  SignalsPayload,
  SnapshotPayload,
} from '../types/cgBoost';

// Re-export so existing imports keep working without churning every consumer.
export * from '../types/cgBoost';

const CG_BASE = '/api/cg-boost';

export const PLATFORM_LABELS: Record<CgBoostPlatform, string> = {
  tiktok: 'TikTok',
  meta: 'Instagram/Meta',
  youtube: 'YouTube Shorts',
};

export async function checkCgBoostStatus(): Promise<boolean> {
  try {
    await apiRequest(`${CG_BASE}/status`);
    return true;
  } catch {
    return false;
  }
}

export async function listCampaigns(status?: CgBoostCampaignStatus): Promise<CgBoostCampaign[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const body = await apiRequest(`${CG_BASE}/campaigns${qs}`);
  return body.campaigns;
}

export async function createCampaign(payload: CreateCampaignRequest): Promise<CgBoostCampaign> {
  const body = await apiRequest(`${CG_BASE}/campaigns`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return body.campaign as CgBoostCampaign;
}

export interface CampaignPatch {
  campaignName?: string;
  industryVertical?: string;
  endDate?: string;
  status?: CgBoostCampaignStatus;
  plannedBudget?: { tiktok?: number; meta?: number; youtube?: number };
  platformIds?: CgBoostCampaign['platformIds'];
  finalReportGenerated?: boolean;
  finalReportDriveFileId?: string;
}

export async function patchCampaign(
  canonicalCampaignId: string,
  patch: CampaignPatch,
): Promise<CgBoostCampaign> {
  const body = await apiRequest(
    `${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
    },
  );
  return body.campaign as CgBoostCampaign;
}

export async function getSnapshot(canonicalCampaignId: string): Promise<SnapshotPayload> {
  return apiRequest(`${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}/snapshot`);
}

export async function getPlatforms(canonicalCampaignId: string): Promise<PlatformsPayload> {
  return apiRequest(`${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}/platforms`);
}

export async function getCreative(canonicalCampaignId: string): Promise<CreativePayload> {
  return apiRequest(`${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}/assets`);
}

export async function getMomentum(canonicalCampaignId: string): Promise<MomentumPayload> {
  return apiRequest(`${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}/momentum`);
}

export async function getSignals(canonicalCampaignId: string): Promise<SignalsPayload> {
  return apiRequest(`${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}/signals`);
}

export async function getIntelligence(canonicalCampaignId: string): Promise<IntelligencePayload> {
  return apiRequest(`${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}/intelligence`);
}

export async function getPullStatus(canonicalCampaignId: string): Promise<CgBoostPullStatus[]> {
  const body = await apiRequest(
    `${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}/pull-status`,
  );
  return body.statuses as CgBoostPullStatus[];
}

export async function refreshCampaign(
  canonicalCampaignId: string,
  options?: { date?: string; full?: boolean },
): Promise<{ outcome: unknown }> {
  return apiRequest(`${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}/refresh`, {
    method: 'POST',
    body: JSON.stringify(options ?? {}),
  });
}

// ----- Platform link wizard -----

export interface ParsedPlatformLink {
  platform: CgBoostPlatform;
  ids: NonNullable<CgBoostPlatformIds[CgBoostPlatform]>;
}

/**
 * Parse a copy-pasted ad-manager URL into platformIds. Returns null when the
 * URL doesn't match any known shape (the wizard then falls back to manual
 * entry without surfacing an error).
 */
export async function parsePlatformLinkUrl(url: string): Promise<ParsedPlatformLink | null> {
  try {
    const body = await apiRequest(`${CG_BASE}/platform-links/parse`, {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
    return body as ParsedPlatformLink;
  } catch {
    return null;
  }
}

/**
 * Dry-run validate platformIds against the platform's API. Always resolves —
 * surface `validated: false` to the user as "couldn't check" rather than as
 * an error so they can still proceed when creds aren't provisioned yet.
 */
export async function validatePlatformLink(
  platform: CgBoostPlatform,
  ids: Record<string, string>,
): Promise<CgBoostValidateLinkResult> {
  return apiRequest(`${CG_BASE}/platform-links/validate`, {
    method: 'POST',
    body: JSON.stringify({ platform, ids }),
  });
}

// ----- Asset patch -----

export interface AssetPatchPayload {
  creatorName?: string;
  format?: string;
  hookStyle?: string;
  discoverySource?: 'confirmed' | 'hidden';
  /** String binds to that group; explicit null ungroups. */
  creativeGroupId?: string | null;
}

export async function patchAsset(
  canonicalAssetId: string,
  patch: AssetPatchPayload,
): Promise<unknown> {
  const body = await apiRequest(`${CG_BASE}/assets/${encodeURIComponent(canonicalAssetId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return body.asset;
}

// ----- Creative groups -----

export async function createCreativeGroup(
  canonicalCampaignId: string,
  assetIds: string[],
): Promise<{ creativeGroupId: string }> {
  return apiRequest(
    `${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}/creative-groups`,
    { method: 'POST', body: JSON.stringify({ assetIds }) },
  );
}

export async function dissolveCreativeGroup(
  canonicalCampaignId: string,
  creativeGroupId: string,
): Promise<void> {
  await apiRequest(
    `${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}/creative-groups/${encodeURIComponent(creativeGroupId)}`,
    { method: 'DELETE' },
  );
}

export async function getCreativeGroupSuggestions(
  canonicalCampaignId: string,
): Promise<CreativeGroupSuggestion[]> {
  const body = await apiRequest(
    `${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}/creative-groups/suggestions`,
  );
  return body.suggestions as CreativeGroupSuggestion[];
}

export async function dismissCreativeGroupSuggestion(
  canonicalCampaignId: string,
  suggestionId: string,
): Promise<void> {
  await apiRequest(
    `${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}/creative-groups/dismissals`,
    { method: 'POST', body: JSON.stringify({ suggestionId }) },
  );
}

// ----- Change log -----

export async function getCampaignChangeLog(
  canonicalCampaignId: string,
): Promise<CgBoostChangeLogEntry[]> {
  const body = await apiRequest(
    `${CG_BASE}/campaigns/${encodeURIComponent(canonicalCampaignId)}/changelog`,
  );
  return body.entries as CgBoostChangeLogEntry[];
}

export async function getAssetChangeLog(
  canonicalAssetId: string,
): Promise<CgBoostChangeLogEntry[]> {
  const body = await apiRequest(
    `${CG_BASE}/assets/${encodeURIComponent(canonicalAssetId)}/changelog`,
  );
  return body.entries as CgBoostChangeLogEntry[];
}
